/**
 * Payment state is derived. It never writes appointments.status.
 *
 * prepaid: money or a clip is already held, and the visit is not completed.
 * paid: the visit is completed and covered, or a punch-card redemption is
 * tied to a completed visit.
 */
import type { AppointmentLifecycleStatus } from '@/utils/appointmentStatus';

export type VisitPaymentState =
  | 'unpaid'
  | 'prepaid'
  | 'partly_paid'
  | 'paid'
  | 'refunded'
  | 'forfeited'
  | 'credited';

export interface DeriveVisitPaymentStateInput {
  lifecycle: AppointmentLifecycleStatus;
  invoiceStatus?: string | null;
  paymentStatus?: string | null;
  /** A punch_card_usages row exists and has not been reverted. */
  punchCardHeld?: boolean;
  /** Set by a later credit-note or cancellation policy. */
  forced?: 'forfeited' | 'credited' | null;
  /** appointments.payment_forfeited_at is set. */
  paymentForfeited?: boolean;
}

const REFUND_INVOICE = new Set(['refunded']);
const PARTIAL_INVOICE = new Set(['partial']);
const PAID_INVOICE = new Set(['paid']);

function visitIsComplete(lifecycle: AppointmentLifecycleStatus): boolean {
  switch (lifecycle) {
    case 'completed':
    case 'treatment_finished':
      return true;
    case 'upcoming':
    case 'checked_in':
    case 'under_treatment':
    case 'no_show':
    case 'cancelled':
      return false;
    default: {
      const _exhaustive: never = lifecycle;
      return _exhaustive;
    }
  }
}

export function deriveVisitPaymentState(input: DeriveVisitPaymentStateInput): VisitPaymentState {
  if (input.forced === 'forfeited' || input.forced === 'credited') return input.forced;
  if (input.paymentForfeited) return 'forfeited';

  const invoice = input.invoiceStatus ?? null;
  if (invoice === 'credited') return 'credited';
  const payment = input.paymentStatus ?? null;
  const covered =
    Boolean(input.punchCardHeld) ||
    payment === 'paid' ||
    (invoice != null && PAID_INVOICE.has(invoice));
  const closed =
    input.lifecycle === 'cancelled' || input.lifecycle === 'no_show';

  if (payment === 'refunded' || (invoice != null && REFUND_INVOICE.has(invoice))) {
    return 'refunded';
  }
  if (closed) {
    return covered || (invoice != null && PARTIAL_INVOICE.has(invoice)) ? 'paid' : 'unpaid';
  }
  if (invoice != null && PARTIAL_INVOICE.has(invoice)) {
    return visitIsComplete(input.lifecycle) ? 'partly_paid' : 'prepaid';
  }
  if (covered) {
    return visitIsComplete(input.lifecycle) ? 'paid' : 'prepaid';
  }
  return 'unpaid';
}

export function visitPaymentStateLabelKey(state: VisitPaymentState): string {
  switch (state) {
    case 'unpaid':
      return 'appointments.payment.state_unpaid';
    case 'prepaid':
      return 'appointments.payment.state_prepaid';
    case 'partly_paid':
      return 'appointments.payment.state_partly_paid';
    case 'paid':
      return 'appointments.payment.state_paid';
    case 'refunded':
      return 'appointments.payment.state_refunded';
    case 'forfeited':
      return 'appointments.payment.state_forfeited';
    case 'credited':
      return 'appointments.payment.state_credited';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}
